import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useDebouncedValue } from "../../../hooks/use-debounced-value";
import {
  escalateKybVerification,
  exportKybBusinesses,
  updateKybBusinessStatus,
} from "../../../shared/api/kyb";
import { useKybBusinesses } from "../../../shared/api/hooks/useKybBusinesses";
import { DataTable } from "../../../shared/ui/DataTable";
import { BodyText, Button, PaginationControls, Shimmer } from "../../../shared/ui";
import { cn, getApiErrorMessage } from "../../../shared/lib";
import { PAGES } from "../../../shared/lib/pageUrlConstants";
import {
  KYB_BUSINESS_TYPE_OPTIONS,
  KYB_COUNTRY_OPTIONS,
  KYB_SORT_OPTIONS,
  KYB_STATUS_TABS,
} from "../constants/table";
import {
  KybComplianceRow,
  KybComplianceStatus,
  KybStatusCountMap,
} from "../types";
import KybComplianceEscalateModal from "./KybComplianceEscalateModal";
import KybComplianceEmptyState from "./KybComplianceEmptyState";
import KybComplianceFiltersBar from "./KybComplianceFiltersBar";
import KybComplianceFinishReviewModal from "./KybComplianceFinishReviewModal";
import KybComplianceRejectCustomerModal from "./KybComplianceRejectCustomerModal";
import { createKybComplianceColumns } from "./kybComplianceColumns";

const PAGE_SIZE = 7;

type KybComplianceTableSectionProps = {
  statusCounts?: KybStatusCountMap;
  businessSource: string;
};

const KybComplianceTableSection = ({
  statusCounts,
  businessSource,
}: KybComplianceTableSectionProps) => {
  const navigate = useNavigate();
  const [activeStatus, setActiveStatus] =
    useState<KybComplianceStatus>("under-review");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("newest");
  const [businessType, setBusinessType] = useState("all");
  const [country, setCountry] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedActionRow, setSelectedActionRow] =
    useState<KybComplianceRow | null>(null);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isEscalateModalOpen, setIsEscalateModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [updatingBusinessStatus, setUpdatingBusinessStatus] = useState<
    "APPROVED" | "REJECTED" | null
  >(null);
  const [isEscalating, setIsEscalating] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const debouncedQuery = useDebouncedValue(query, 400);
  const { items, meta, isLoading, error, refetch } = useKybBusinesses({
    search: debouncedQuery.trim() || undefined,
    sort,
    country,
    status: activeStatus,
    businessType,
    businessSource,
    page: currentPage,
    limit: PAGE_SIZE,
  });
  const totalPages = Math.max(
    1,
    meta?.totalPages ??
      (typeof meta?.total === "number"
        ? Math.ceil(meta.total / PAGE_SIZE)
        : items.length > 0 && items.length < PAGE_SIZE
          ? currentPage
          : 1),
  );
  const statusTabs = useMemo(
    () =>
      KYB_STATUS_TABS.map((tab) => ({
        ...tab,
        count: statusCounts?.[tab.value] ?? tab.count,
      })),
    [statusCounts],
  );
  const columns = useMemo(
    () =>
      createKybComplianceColumns(activeStatus, {
        onApproveKyc: (row) => {
          setSelectedActionRow(row);
          setIsApproveModalOpen(true);
        },
        onEscalate: (row) => {
          setSelectedActionRow(row);
          setIsEscalateModalOpen(true);
        },
        onRejectKyc: (row) => {
          setSelectedActionRow(row);
          setRejectReason("");
          setIsRejectModalOpen(true);
        },
        onOpenDetails: (row) => {
          navigate(`${PAGES.kybCompliance}/${row.id}`, {
            state: {
              row,
              listStatus: activeStatus,
              businessIds: items.map((item) => item.id),
            },
          });
        },
      }),
    [activeStatus, items, navigate],
  );
  const hasActiveFilters =
    Boolean(debouncedQuery.trim()) ||
    sort !== "newest" ||
    businessType !== "all" ||
    country !== "all";
  const activeStatusLabel =
    statusTabs.find((tab) => tab.value === activeStatus)?.label ?? "";

  useEffect(() => {
    setCurrentPage((prev) => Math.min(prev, totalPages));
  }, [totalPages]);

  const handleStatusChange = (status: KybComplianceStatus) => {
    setActiveStatus(status);
    setCurrentPage(1);
  };

  const handleQueryChange = (value: string) => {
    setQuery(value);
    setCurrentPage(1);
  };

  const handleSortChange = (value: string) => {
    setSort(value);
    setCurrentPage(1);
  };

  const handleBusinessTypeChange = (value: string) => {
    setBusinessType(value);
    setCurrentPage(1);
  };

  const handleCountryChange = (value: string) => {
    setCountry(value);
    setCurrentPage(1);
  };

  const handleExport = async () => {
    setIsExporting(true);

    try {
      const { blob, filename } = await exportKybBusinesses({
        search: debouncedQuery.trim() || undefined,
        sort,
        country,
        status: activeStatus,
        businessType,
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Unable to export KYB records."));
    } finally {
      setIsExporting(false);
    }
  };

  const clearSelectedActionRow = () => {
    setSelectedActionRow(null);
  };

  const submitBusinessStatus = async (
    status: "APPROVED" | "REJECTED",
    reason?: string,
  ) => {
    if (!selectedActionRow) return;

    setUpdatingBusinessStatus(status);

    try {
      await updateKybBusinessStatus({
        businessId: selectedActionRow.id,
        status,
        reason,
      });
      toast.success(
        status === "APPROVED"
          ? "Customer approved successfully."
          : "Customer rejected successfully.",
      );
      setIsApproveModalOpen(false);
      setIsRejectModalOpen(false);
      setRejectReason("");
      clearSelectedActionRow();
      refetch();
    } catch (error) {
      toast.error(
        getApiErrorMessage(
          error,
          status === "APPROVED"
            ? "Unable to approve customer."
            : "Unable to reject customer.",
        ),
      );
    } finally {
      setUpdatingBusinessStatus(null);
    }
  };

  const submitEscalation = async ({
    recipients,
    role,
    roleName,
    note,
  }: {
    recipients?: Array<{ email: string; firstName: string }>;
    role?: string;
    roleName?: string;
    note?: string;
  }) => {
    if (!selectedActionRow || (!role && !recipients?.length)) return;

    setIsEscalating(true);

    try {
      if (role) {
        await escalateKybVerification({
          businessId: selectedActionRow.id,
          link: `${window.location.origin}${PAGES.kybCompliance}/${selectedActionRow.id}`,
          businessName: selectedActionRow.businessName,
          role,
          roleName,
          note,
        });
      } else {
        await Promise.all(
          (recipients ?? []).map((recipient) =>
            escalateKybVerification({
              businessId: selectedActionRow.id,
              link: `${window.location.origin}${PAGES.kybCompliance}/${selectedActionRow.id}`,
              adminEmail: recipient.email,
              businessName: selectedActionRow.businessName,
              adminFirstName: recipient.firstName,
              note,
            }),
          ),
        );
      }
      toast.success("KYB verification escalated successfully.");
      setIsEscalateModalOpen(false);
      clearSelectedActionRow();
    } catch (error) {
      toast.error(
        getApiErrorMessage(error, "Unable to escalate KYB verification."),
      );
    } finally {
      setIsEscalating(false);
    }
  };

  return (
    <section className="mt-8 w-full">
      <div className="border-b border-[#E4E7F4]">
        <div className="flex items-center gap-6 overflow-x-auto">
          {statusTabs.map((tab) => {
            const isActive = tab.value === activeStatus;

            return (
              <button
                key={tab.value}
                type="button"
                onClick={() => handleStatusChange(tab.value)}
                className="shrink-0"
              >
                <BodyText
                  className={cn(
                    "border-b-2 px-1 pb-2 text-[16px] font-[400] transition-colors whitespace-nowrap",
                    isActive
                      ? "border-primary-20 text-gray-30"
                      : "border-transparent text-gray-20 hover:text-gray-30",
                  )}
                >
                  {tab.label}
                  <span className="ml-2 text-[#7A7FAA]">{tab.count}</span>
                </BodyText>
              </button>
            );
          })}
        </div>
      </div>

      <KybComplianceFiltersBar
        query={query}
        onQueryChange={handleQueryChange}
        sort={sort}
        onSortChange={handleSortChange}
        sortOptions={KYB_SORT_OPTIONS}
        businessType={businessType}
        onBusinessTypeChange={handleBusinessTypeChange}
        businessTypeOptions={KYB_BUSINESS_TYPE_OPTIONS}
        country={country}
        onCountryChange={handleCountryChange}
        countryOptions={KYB_COUNTRY_OPTIONS}
        isExporting={isExporting}
        onExport={handleExport}
      />

      {isLoading ? (
        <DataTable
          columns={columns}
          data={[]}
          getRowKey={(row) => row.id}
          emptyState={
            <div className="space-y-3 py-2">
              {Array.from({ length: 5 }).map((_, index) => (
                <Shimmer key={index} className="h-8 w-full rounded-[10px]" />
              ))}
            </div>
          }
        />
      ) : error ? (
        <DataTable
          columns={columns}
          data={[]}
          getRowKey={(row) => row.id}
          emptyState={
            <div className="space-y-3 py-2">
              <div className="text-[14px] text-[#C24E4E]">{error}</div>
              <Button
                type="button"
                variant="outline"
                className="h-10 rounded-[12px]"
                onClick={refetch}
              >
                Retry
              </Button>
            </div>
          }
        />
      ) : items.length === 0 ? (
        <KybComplianceEmptyState
          variant={hasActiveFilters ? "search" : "none"}
          statusLabel={activeStatusLabel}
        />
      ) : (
        <DataTable
          columns={columns}
          data={items}
          getRowKey={(row) => row.id}
          onRowClick={
            activeStatus === "not-started"
              ? undefined
              : (row) =>
                  navigate(`${PAGES.kybCompliance}/${row.id}`, {
                    state: {
                      row,
                      listStatus: activeStatus,
                      businessIds: items.map((item) => item.id),
                    },
                  })
          }
        />
      )}

      {!isLoading && !error && items.length > 0 ? (
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          className="mt-8 flex items-center gap-2"
        />
      ) : null}

      <KybComplianceFinishReviewModal
        open={isApproveModalOpen}
        businessName={selectedActionRow?.businessName ?? "this business"}
        flaggedItemsCount={0}
        isSubmitting={updatingBusinessStatus === "APPROVED"}
        onOpenChange={(open) => {
          if (updatingBusinessStatus) return;
          setIsApproveModalOpen(open);
          if (!open) clearSelectedActionRow();
        }}
        onConfirm={() => submitBusinessStatus("APPROVED")}
      />

      <KybComplianceEscalateModal
        open={isEscalateModalOpen}
        isSubmitting={isEscalating}
        onOpenChange={(open) => {
          if (isEscalating) return;
          setIsEscalateModalOpen(open);
          if (!open) clearSelectedActionRow();
        }}
        onSubmit={submitEscalation}
      />

      <KybComplianceRejectCustomerModal
        open={isRejectModalOpen}
        reason={rejectReason}
        isSubmitting={updatingBusinessStatus === "REJECTED"}
        onOpenChange={(open) => {
          if (updatingBusinessStatus === "REJECTED") return;
          setIsRejectModalOpen(open);
          if (!open) {
            setRejectReason("");
            clearSelectedActionRow();
          }
        }}
        onReasonChange={setRejectReason}
        onSubmit={() => submitBusinessStatus("REJECTED", rejectReason)}
      />
    </section>
  );
};

export default KybComplianceTableSection;
